import type { ReactElement } from "react";
import { Rect } from "react-konva";
import { BACKGROUND_TYPE, COLORS, type BackgroundType } from "@design-lab/shared/constants";
import { LAB_TYPE, type LabType } from "@design-lab/shared/types/labType";

type BackgroundRectProps = {
  width: number;
  height: number;
  backgroundType: BackgroundType;
  labType?: LabType;
};

export function BackgroundRect({
  width,
  height,
  backgroundType,
  labType = LAB_TYPE.DESIGN
}: BackgroundRectProps) {
  if (backgroundType === BACKGROUND_TYPE.CHECKERED) {
    // Return checkered pattern
    return <CheckeredBackground width={width} height={height} />;
  }

  if (backgroundType === BACKGROUND_TYPE.GREEN) {
    // Return green screen
    return <Rect width={width} height={height} fill={COLORS.GREEN_SCREEN} />;
  }

  if (backgroundType === BACKGROUND_TYPE.GRAY) {
    // Return gray background (transfers lab uses gray instead of white)
    return <Rect width={width} height={height} fill={COLORS.GRAY_BACKGROUND} />;
  }

  // Default background - gray for transfers lab, white for design lab
  const defaultFill =
    labType === LAB_TYPE.TRANSFERS ? COLORS.GRAY_BACKGROUND : COLORS.WHITE_BACKGROUND;

  return <Rect width={width} height={height} fill={defaultFill} />;
}

function CheckeredBackground({ width, height }: { width: number; height: number }) {
  const cellSize = 20;
  const cells: ReactElement[] = [];

  for (let y = 0; y < Math.ceil(height / cellSize); y += 1) {
    for (let x = 0; x < Math.ceil(width / cellSize); x += 1) {
      const isEven = (x + y) % 2 === 0;
      cells.push(
        <Rect
          key={`${x}-${y}`}
          x={x * cellSize}
          y={y * cellSize}
          width={cellSize}
          height={cellSize}
          fill={isEven ? COLORS.GRAY_BACKGROUND : COLORS.WHITE_BACKGROUND}
        />
      );
    }
  }

  return <>{cells}</>;
}
